import React, { useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { AuthCard } from '../authV2/components/constants'
import { LoginForm } from '../authV2/components'
import { useRmnBrand } from '../../layouts/RmnBrand/RmnBrandContext'
import { RmnThemeProvider } from '../../layouts/RmnBrand/RmnThemeProvider'
import { useUser } from '../../hooks/useUser'
import { portalBasePath } from '../../utils/portalPath'
import { GoogleButton } from '../../components/auth/GoogleButton'

// Branded login for the RMN white-label portal. Same auth flow as /login
// (LoginForm → POST /auth/login) — only the shell differs: brand logo,
// system name, brand colors. The front door keeps its white-label branding
// (it wraps itself in RmnThemeProvider and sets the branded document.title)
// even though the authenticated pages moved to the main portal design
// (TDC-1499).
export default function RmnLoginPage() {
  const navigate = useNavigate()
  const { brand, palette } = useRmnBrand()
  const { user } = useUser()
  const { brandSlug } = useParams()

  useEffect(() => {
    if (!brand) return undefined
    const prevTitle = document.title
    document.title = palette.systemName
    return () => {
      document.title = prevTitle
    }
  }, [brand, palette])

  const queryParams = new URLSearchParams(window.location.search)
  // Default landing = the portal index (role-aware: brand roles go
  // slug-free, staff stay in the workspace).
  const redirectPath =
    queryParams.get('redirect') || portalBasePath(String(brandSlug))

  // Already logged in: forward straight to the target page.
  if (user) {
    return <Navigate to={redirectPath} replace />
  }

  return (
    <RmnThemeProvider palette={palette}>
      <Box className="flex-1 flex flex-col items-center justify-center gap-4 py-12">
        {brand?.logo && (
          <Box className="w-[220px]">
            <img className="size-full object-contain" alt={brand.name} src={brand.logo} />
          </Box>
        )}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: 'var(--rmn-primary)' }}
        >
          {palette.systemName}
        </Typography>
        {palette.loginTagline && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {palette.loginTagline}
          </Typography>
        )}
        <AuthCard elevation={0} variant="outlined">
          <Box textAlign="center" display="flex" flexDirection="column" gap={5}>
            <Typography variant="h5">Log in</Typography>
            <LoginForm onSuccess={() => navigate(redirectPath)} />
            {/* Sign-in only on a branded login: a Google email with no
                account is told to ask the network admin for an invite. */}
            <GoogleButton intent="login" rmnSlug={String(brandSlug)} />
          </Box>
        </AuthCard>
        <Typography variant="caption" sx={{ opacity: 0.6, mt: 2 }}>
          Powered by The Desire Company
        </Typography>
      </Box>
    </RmnThemeProvider>
  )
}
